import React, { useEffect, useState } from 'react';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useUser } from '@src/state/user/index';
import { usePAARegistration } from '@src/state/PAARegistration/index';
import { useOnloading } from '@src/state/landing/index';
import { useTIN } from '@src/state/taxInformation/hooks';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { UserRegistrationShell } from '@src/common/UserRegistrationShell';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { adobeInteraction, getOrgType } from '@src/common/helpers';
import { config } from '@uhg-abyss/web/tools/config';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useDental } from '@src/state/dental/hooks';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '10px',
  fontFamily: '$primary',
});

const TooltipText = styled('p', {
  fontSize: '16px',
  fontWeight: 600,
  marginBottom: '10px',
  lineHeight: '1.3',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const AlertDisplay = styled('div', {
  marginBottom: '2%',
  width: '100%',
});

const AlertDescription = styled('div', {
  fontSize: 14,
  fontWeight: 600,
});

const AlertCode = styled('div', {
  fontSize: 10,
  color: '$gray8',
});

const PAAToolTip = styled('span', {
  color: '$info1',
  display: 'inline-block',
  paddingBottom: '1px',
  borderBottom: '1px dashed #196ECF',
  fontWeight: 400,
  '&:hover': {
    cursor: 'pointer',
  },
});

const PaaToolTipText = styled('div', {
  display: 'inline-block',
});

const Separator = styled('hr', {
  overflow: 'visible',
  padding: 0,
  border: 'none',
  borderTop: 'solid thin #c9ced3',
  color: 'rgb(168, 167, 167)',
  textAlign: 'center',
  marginTop: '35px',
  marginBottom: '35px',
  width: '70%',
  '&:after': {
    content: "'OR'",
    display: 'inline-block',
    position: 'relative',
    fontWeight: 900,
    top: '-1.2em',
    fontSize: '12px',
    padding: '0 0.5em',
    background: 'white',
  },
});

const isValidEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const PAADesignate = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const errorMessages = lagoonContext.getTable('error-messaging');

  const form = useForm();
  const { navigate } = useRouter();
  const { token } = useParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [unknownPAA, setUnknownPAA] = useState(false);
  const [designatePAAError, setDesignatePAAError] = useState(null);
  const { currentFlow } = useOnloading.state();
  const { TINNumber, TINInfo } = useTIN.state();
  const { designatePAA } = usePAARegistration.actions();
  const { designatePAASaga } = usePAARegistration.state();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { isDental } = useDental.state();

  const resetDesignatePAAForm = () => {
    form.setValue('PAADesignate.firstName', '');
    form.setValue('PAADesignate.lastName', '');
    form.setValue('PAADesignate.email', '');
  };

  useEffect(() => {
    resetDesignatePAAForm();
    form.setValue('PAADesignate.PAAUnknown', false);

    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'paa designate';
      const pageName = `digitalsecurity:${section1}:${section2}`;

      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
      });
    }
  }, []);

  const handleError = ({ error }) => {
    if (error) {
      return setDesignatePAAError(error);
    }
    return navigate(`/error-page/${token}`);
  };

  const processResponse = (res) => {
    adobeInteraction(
      'paa designate',
      'next',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
    );
    navigate(`/PAADesignate/EmailConfirmation/${token}`);
  };

  const handleSubmit = (e) => {
    if (unknownPAA) {
      navigate(`/PAARequired/${token}`);
      return;
    }
    form.validate('PAADesignate.firstName');
    form.validate('PAADesignate.lastName');
    form.validate('PAADesignate.email');
    setDesignatePAAError(null);

    const fieldToFocus = [];
    if (!firstName) fieldToFocus.push('PAADesignate.firstName');
    if (!lastName) fieldToFocus.push('PAADesignate.lastName');
    if (!isValidEmail(email)) fieldToFocus.push('PAADesignate.email');

    if (fieldToFocus.length === 0) fieldToFocus.push('PAADesignate.firstName');

    form.validate(
      fieldToFocus[0],
      () => {
        const payload = {
          requestId: TINInfo.requestId,
          currentFlow,
          userUUID,
          tin: TINNumber,
          firstName,
          lastName,
          email,
          url:
            uiMessages.getRow('REGISTER_API')?.message ||
            config('REGISTER_API'),
          isDental,
        };
        designatePAA(
          payload,
          (response) => {
            if (response.error) {
              if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
                adobeTaggingEvent('API_TRANSACTION_ERROR', {
                  errorMessage: response.error.description,
                  errorApiCode: response.error.code,
                  errorHttpsCode: response.error.status,
                });
              }
              handleError(response);
              return;
            }
            processResponse();
          },
          handleError
        );
      },
      (error) => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const fields = fieldToFocus;
          adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const paUserTitle = isAPIMarketPlaceUser
    ? 'Secret Keeper'
    : 'Primary Access Administrator';

  const handleUnknownPAA = (e) => {
    resetDesignatePAAForm();
    setUnknownPAA(e.target.checked);
    if (e.target.checked) {
      form.clearErrors();
    }
  };

  return (
    <LoadingOverlay isLoading={designatePAASaga.isLoading}>
      <UserRegistrationShell activeStep="paa designate">
        <MainContainer>
          <BoxHeader>
            <BoxHeaderText>
              {isAPIMarketPlaceUser
                ? 'Designate a Secret Keeper.'
                : 'Designate a Primary Access Administrator.'}
              {''}
            </BoxHeaderText>
          </BoxHeader>
          <Description>
            In order for your organization to gain access to the
            UnitedHealthcare
            {isAPIMarketPlaceUser ? ' API Marketplace' : ' Provider Portal'},
            you will need to designate someone as the organization's{' '}
            <PaaToolTipText>
              <Tooltip
                id="tooltip"
                placement="bottom"
                aria-label="tooltip label"
                aria-describedby="tooltip described by"
                width={500}
                asChild={false}
                content={
                  isAPIMarketPlaceUser ? (
                    <React.Fragment>
                      <TooltipText>
                        <strong>Secret Keeper</strong> functions as the primary
                        link between your organization and UnitedHealthcare API
                        Marketplace.
                      </TooltipText>
                      <TooltipText>
                        Secret Keeper is responsible for maintaining the
                        organization’s API credentials, generating new API
                        credentials, as needed.
                      </TooltipText>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <TooltipText>
                        <strong>Primary Access Administrator (PAA)</strong>{' '}
                        functions as the primary link between your organization
                        and UnitedHealthCare.
                      </TooltipText>
                      <TooltipText>
                        Your PAA is responsible for managing security, setting
                        up and maintaining user accounts, updating organization
                        information, and performing other administrative tasks
                        in the Portal.
                      </TooltipText>
                    </React.Fragment>
                  )
                }
              >
                <PAAToolTip>
                  {isAPIMarketPlaceUser
                    ? 'Secret Keeper.'
                    : 'Primary Access Administrator.'}
                  &nbsp;
                </PAAToolTip>{' '}
              </Tooltip>{' '}
            </PaaToolTipText>
            If you know who that should be, please enter their contact
            information below.
          </Description>
          {designatePAAError ? (
            <Flex>
              <React.Fragment>
                <AlertDisplay>
                  <Alert variant="error" title="Error">
                    <AlertDescription>
                      {errorMessages.getRow(designatePAAError.code)?.message ||
                        errorMessages.getRow('DEFAULT')?.message}
                    </AlertDescription>
                    <AlertCode>
                      ({designatePAAError.code}, {designatePAAError.timestamp})
                    </AlertCode>
                  </Alert>
                </AlertDisplay>
              </React.Fragment>
            </Flex>
          ) : null}
          <div>
            <div
              style={{
                width: '70%',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <FormProvider state={form}>
                <Flex justify="space-between" css={{ marginBottom: '15px' }}>
                  <div style={{ marginRight: '15px' }}>
                    <TextInput
                      id="PAADesignate-firstName"
                      label="First Name"
                      model="PAADesignate.firstName"
                      aria-label="type here to enter First Name"
                      validators={{
                        required: !unknownPAA
                          ? (value) => {
                              return !value && !unknownPAA;
                            }
                          : false,
                      }}
                      value={firstName}
                      errorMessage="First name can not be blank."
                      isDisabled={!!unknownPAA}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        form.validate('PAADesignate.firstName');
                      }}
                      onClear={(e) => {
                        setFirstName('');
                      }}
                      isClearable
                      css={{
                        'abyss-text-input-label': {
                          fontWeight: 'normal',
                        },
                      }}
                    />
                  </div>
                  <div>
                    {' '}
                    <TextInput
                      id="PAADesignate-lastName"
                      label="Last Name"
                      model="PAADesignate.lastName"
                      aria-label="type here to enter Last Name"
                      validators={{
                        required: !unknownPAA
                          ? (value) => {
                              return !value && !unknownPAA;
                            }
                          : false,
                      }}
                      value={lastName}
                      isDisabled={!!unknownPAA}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        form.validate('PAADesignate.lastName');
                      }}
                      onClear={(e) => {
                        setLastName('');
                      }}
                      isClearable
                      errorMessage="Last name can not be blank."
                      css={{
                        'abyss-text-input-label': {
                          fontWeight: 'normal',
                        },
                      }}
                    />
                  </div>
                </Flex>
                <TextInput
                  id="PAADesignate-email"
                  label="Email Address"
                  model="PAADesignate.email"
                  type="email"
                  aria-label="type here to enter Email Address"
                  validators={{
                    required: !unknownPAA
                      ? (value) => {
                          return !value && !unknownPAA;
                        }
                      : false,
                    pattern: {
                      value:
                        /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i,
                      message: 'Email address is not valid',
                    },
                  }}
                  isDisabled={!!unknownPAA}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    form.validate('PAADesignate.email');
                  }}
                  isClearable
                  css={{
                    'abyss-text-input-label': {
                      fontWeight: 'normal',
                    },
                  }}
                />
              </FormProvider>
            </div>
          </div>
          <Separator />
          <FormProvider state={form}>
            <Checkbox
              data-testid="PAAUnknown"
              id="PAAUnknown"
              model="PAADesignate.PAAUnknown"
              size="md"
              label={`I don't know who the ${paUserTitle} is.`}
              onChange={handleUnknownPAA}
              isChecked={!unknownPAA}
              aria-label="I don't know who the PAA is."
              css={{
                'abyss-checkbox-label': {
                  fontWeight: 'normal',
                },
              }}
            />
          </FormProvider>
        </MainContainer>
        <Flex justify="space-between">
          <BackButton
            onClick={() => {
              adobeInteraction(
                'paa designate',
                'back',
                currentFlow,
                featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
              );
              navigate(-1);
            }}
          >
            <IconSymbol icon="arrow_back" size={20} color="$primary1" />
            <BackButtonText>Back</BackButtonText>
          </BackButton>
          <NextButton onClick={handleSubmit}>
            <NextButtonText>Next</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex>
      </UserRegistrationShell>{' '}
    </LoadingOverlay>
  );
};
